import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../axiosConfig";
import { useGuestPreview } from "../context/GuestPreviewContext";

const GUEST_ID_KEY = "gradconnect_guest_id";
const GUEST_INTERESTS_KEY = "gradconnect_guest_research_interests";
const SUGGESTED_INTERESTS = [
  "surgical robotics",
  "reinforcement learning",
  "climate modeling",
];

/**
 * Hero research bar + "Find matches" — used on Normal / Medium / Small home layouts.
 * layout: "normal" | "medium" | "centered"
 */
const HeroFindMatches = ({ layout = "normal", buttonBelow = false }) => {
  const [interests, setInterests] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setGuestPreview } = useGuestPreview();

  const inputClass =
    layout === "centered"
      ? "w-full min-w-0 bg-transparent pl-3 pr-5 py-2.5 text-gray-800 placeholder:text-gray-400 focus:outline-none"
      : layout === "medium"
      ? "w-full min-w-0 bg-transparent pl-3 pr-5 py-2.5 text-gray-800 placeholder:text-gray-400 focus:outline-none"
      : "w-full min-w-0 bg-transparent pl-3 pr-5 py-2.5 text-gray-800 placeholder:text-gray-400 focus:outline-none";
  const inputClassWithSize = `${inputClass} ${
    buttonBelow ? "text-sm placeholder:text-sm py-3" : ""
  }`;

  const buttonClass =
    layout === "centered"
      ? "shrink-0 rounded-full bg-[#0196BF] text-white font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-1.5 hover:bg-[#017BA3] transition disabled:opacity-60 whitespace-nowrap"
      : layout === "medium"
      ? "shrink-0 rounded-full bg-[#0196BF] text-white font-semibold text-xs px-3.5 py-1.5 hover:bg-[#017BA3] transition disabled:opacity-60 whitespace-nowrap"
      : "shrink-0 rounded-full bg-[#0196BF] text-white font-semibold text-xs px-3.5 py-1.5 hover:bg-[#017BA3] transition z-20 disabled:opacity-60 whitespace-nowrap";
  const insideCircleButtonClass =
    "ml-1 mr-[-2px] inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0196BF] text-white transition hover:bg-[#017BA3] disabled:opacity-60 [&>svg]:block";

  const searchBarRowClass =
    layout === "centered"
      ? "mt-6 w-full max-w-xl mx-auto flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)] focus-within:border-[#0196BF]/60 focus-within:ring-2 focus-within:ring-[#0196BF]/20"
      : layout === "medium"
      ? "mt-6 w-full max-w-md flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)] focus-within:border-[#0196BF]/60 focus-within:ring-2 focus-within:ring-[#0196BF]/20"
      : "mt-6 w-full max-w-lg flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)] focus-within:border-[#0196BF]/60 focus-within:ring-2 focus-within:ring-[#0196BF]/20";
  const searchBarRowClassWithSpacing = `${searchBarRowClass} ${
    buttonBelow ? "mt-0 max-w-none" : ""
  }`;

  const handleFindMatches = async () => {
    const text = interests.trim();
    if (!text) {
      setError("Describe your research interests to find matches.");
      return;
    }
    setError("");
    setSubmitting(true);

    try {
      let guestId = sessionStorage.getItem(GUEST_ID_KEY);
      if (!guestId) {
        guestId = crypto.randomUUID();
        sessionStorage.setItem(GUEST_ID_KEY, guestId);
      }
      sessionStorage.setItem(GUEST_INTERESTS_KEY, text);

      const response = await api.post("/guest/preview-initialize/", {
        guest_id: guestId,
        research_interests: text,
        majors: [],
        schools: [],
      });

      setGuestPreview({
        active: true,
        guestId,
        researchInterests: text,
      });

      navigate("/recommendations", {
        state: {
          guestPreview: true,
          guestId,
          researchInterests: text,
          initialRecommendations: response.data,
        },
      });
    } catch (e) {
      const msg =
        e.response?.data?.error ||
        e.message ||
        "Could not load preview. Please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const wrapClass =
    layout === "centered" ? "flex flex-col items-center w-full" : "";
  const isMediumChipMode = layout === "medium" && !buttonBelow;
  const searchPlaceholder = buttonBelow
    ? "Search research interests"
    : "robotics, computer vision, climate modeling";

  const handleInterestChipClick = (term) => {
    setInterests((prev) => {
      const entries = prev
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      const exists = entries.some(
        (item) => item.toLowerCase() === term.toLowerCase()
      );
      if (!exists) entries.push(term);
      return entries.join(", ");
    });
    if (error) setError("");
  };

  return (
    <div className={wrapClass}>
      <label htmlFor="hero-research-interests" className="sr-only">
        Research interests
      </label>
      <div className={searchBarRowClassWithSpacing}>
        <input
          id="hero-research-interests"
          type="text"
          placeholder={searchPlaceholder}
          value={interests}
          onChange={(e) => setInterests(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleFindMatches();
            }
          }}
          className={inputClassWithSize}
        />
        <button
          type="button"
          onClick={handleFindMatches}
          disabled={submitting}
          className={buttonBelow ? insideCircleButtonClass : buttonClass}
          aria-label={submitting ? "Finding matches" : "Find matches"}
        >
          {buttonBelow ? (
            <svg
              aria-hidden
              className="h-[15px] w-[15px] shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12h14M13 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            submitting ? "Finding matches…" : "Find matches"
          )}
        </button>
      </div>
      <div className="mt-3 mb-2 w-full text-sm text-gray-500">
        <p className="mb-1 text-[10px] leading-none font-normal text-gray-400">Try:</p>
        <div
          className={
            buttonBelow
              ? "grid w-full justify-items-start gap-2"
              : isMediumChipMode
              ? "flex w-full max-w-md flex-wrap items-start gap-2"
              : "flex w-full flex-wrap items-start gap-2"
          }
        >
          {SUGGESTED_INTERESTS.map((term) => (
            <button
              key={term}
              type="button"
              onClick={() => handleInterestChipClick(term)}
              className={`rounded-md px-3 py-1 text-left text-sm transition focus:outline-none focus:ring-2 focus:ring-[#0196BF]/30 ${
                buttonBelow
                  ? "inline-flex w-fit bg-[#0196BF]/15 text-[#017BA3] hover:bg-[#0196BF]/25"
                  : isMediumChipMode
                  ? "inline-flex w-fit bg-[#0196BF]/15 text-[#017BA3] hover:bg-[#0196BF]/25"
                  : "inline-flex w-fit bg-gray-100 text-gray-700 hover:bg-[#0196BF]/15 hover:text-[#017BA3]"
              }`}
            >
              {term}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default HeroFindMatches;
